import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { X, Camera, QrCode, Barcode, Loader } from 'lucide-react';

interface BarcodeScannerProps {
  onScan: (value: string) => void;
  onClose: () => void;
  title?: string;
  description?: string;
}

export const BarcodeScanner: React.FC<BarcodeScannerProps> = ({
  onScan,
  onClose,
  title = 'Barcode/QR-Code scannen',
  description = 'Positionieren Sie den Code im Scanbereich'
}) => {
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cameraPermission, setCameraPermission] = useState<boolean | null>(null);
  const [selectedCamera, setSelectedCamera] = useState<string>('');
  const [availableCameras, setAvailableCameras] = useState<Array<{id: string, label: string}>>([]);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const scannerContainerId = 'qr-reader-container';
  const isScanningRef = useRef(false);
  const [isMobile, setIsMobile] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Check if we're on a mobile device
    const checkMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    setIsMobile(checkMobile);

    // Initialize camera list
    const initCameras = async () => {
      try {
        // First check if we have camera permission
        try {
          await navigator.mediaDevices.getUserMedia({ video: true });
          setCameraPermission(true);
        } catch (err) {
          console.error('Camera permission error:', err);
          setCameraPermission(false);
          setError('Kein Zugriff auf die Kamera. Bitte erteilen Sie die Berechtigung.');
          return;
        }
        
        // Then get available cameras
        try {
          const devices = await Html5Qrcode.getCameras();
          setAvailableCameras(devices);
          
          // Select back camera by default if available
          const backCamera = devices.find(device => 
            device.label.toLowerCase().includes('back') || 
            device.label.toLowerCase().includes('hinten') ||
            device.label.toLowerCase().includes('rear')
          );
          
          if (backCamera) {
            setSelectedCamera(backCamera.id);
          } else if (devices.length > 0) {
            setSelectedCamera(devices[0].id);
          }
        } catch (err) {
          console.error('Error getting cameras:', err);
          // Even if we can't get camera list, we might still be able to use the camera
          setCameraPermission(true);
        }
      } catch (err) {
        console.error('Error initializing cameras:', err);
        setCameraPermission(false);
        setError('Kein Zugriff auf die Kamera. Bitte erteilen Sie die Berechtigung.');
      }
    };

    initCameras();

    return () => {
      // Clean up scanner on unmount
      if (scannerRef.current && isScanningRef.current) {
        try {
          scannerRef.current.stop()
            .then(() => {
              console.log('Scanner stopped successfully');
              isScanningRef.current = false;
            })
            .catch(err => {
              console.error('Error stopping scanner:', err);
            });
        } catch (err) {
          console.error('Error in cleanup:', err);
        }
      }
    };
  }, []);

  const startScanning = async () => {
    if (!selectedCamera && !isMobile) return;

    try {
      setIsScanning(true);
      isScanningRef.current = true;
      setError(null);

      if (!scannerRef.current) {
        scannerRef.current = new Html5Qrcode(scannerContainerId);
      }

      // Configure scanner options based on device type
      const scannerConfig = {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0,
        // For mobile devices, use the back camera if available
        ...(isMobile && { facingMode: "environment" })
      };

      // On mobile, we'll use facingMode instead of deviceId
      const cameraIdOrConfig = isMobile 
        ? { facingMode: "environment" } 
        : selectedCamera;

      await scannerRef.current.start(
        cameraIdOrConfig,
        scannerConfig,
        (decodedText) => {
          handleSuccessfulScan(decodedText);
        },
        (errorMessage) => {
          // Ignore errors during scanning as they're usually just frames without codes
        }
      );
    } catch (err) {
      console.error('Scanner initialization error:', err);
      setError('Fehler beim Starten der Kamera. Bitte versuchen Sie es erneut oder verwenden Sie die Dateiauswahl.');
      setIsScanning(false);
      isScanningRef.current = false;
    }
  };

  const handleSuccessfulScan = async (decodedText: string) => {
    if (!isScanningRef.current) return;
    
    isScanningRef.current = false;
    
    try {
      if (scannerRef.current) {
        await scannerRef.current.stop();
      }
      
      onScan(decodedText);
      onClose();
    } catch (err) {
      console.error('Error handling scan:', err);
      onScan(decodedText);
      onClose();
    }
  };

  const handleCameraChange = (cameraId: string) => {
    setSelectedCamera(cameraId);
    if (scannerRef.current && isScanningRef.current) {
      scannerRef.current.stop()
        .then(() => {
          isScanningRef.current = false;
          startScanning();
        })
        .catch(err => {
          console.error('Error switching camera:', err);
          setError('Fehler beim Wechseln der Kamera');
        });
    } else {
      startScanning();
    }
  };

  const requestCameraPermission = () => {
    navigator.mediaDevices.getUserMedia({ video: true })
      .then(() => {
        setCameraPermission(true);
        // Reload camera list
        Html5Qrcode.getCameras()
          .then(devices => {
            setAvailableCameras(devices);
            if (devices.length > 0) {
              // Select back camera by default if available
              const backCamera = devices.find(device => 
                device.label.toLowerCase().includes('back') || 
                device.label.toLowerCase().includes('hinten') ||
                device.label.toLowerCase().includes('rear')
              );
              
              if (backCamera) {
                setSelectedCamera(backCamera.id);
              } else {
                setSelectedCamera(devices[0].id);
              }
            }
          });
      })
      .catch(err => {
        console.error('Camera permission error:', err);
        setError('Kamerazugriff verweigert. Bitte erteilen Sie die Berechtigung in Ihren Browsereinstellungen.');
      });
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      
      // Use Html5Qrcode to scan the file
      if (scannerRef.current) {
        scannerRef.current.clear();
      } else {
        scannerRef.current = new Html5Qrcode(scannerContainerId);
      }
      
      setIsScanning(true);
      setError(null);
      
      Html5Qrcode.scanFile(file, true)
        .then(decodedText => {
          onScan(decodedText);
          onClose();
        })
        .catch(err => {
          console.error('Error scanning file:', err);
          setError('Konnte keinen Code in der Datei erkennen. Bitte versuchen Sie es mit einem anderen Bild.');
          setIsScanning(false);
        });
    }
  };

  useEffect(() => {
    if (selectedCamera && !isScanning && !isScanningRef.current) {
      startScanning();
    }
  }, [selectedCamera]);

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <QrCode className="w-6 h-6 text-blue-600" />
            <span>{title}</span>
          </h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {error && (
          <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-4">
            <p>{error}</p>
            <button 
              onClick={requestCameraPermission}
              className="mt-2 text-sm font-medium text-red-700 underline"
            >
              Kamerazugriff erlauben
            </button>
          </div>
        )}

        {availableCameras.length > 1 && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Kamera auswählen
            </label>
            <select
              value={selectedCamera}
              onChange={(e) => handleCameraChange(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg"
            >
              {availableCameras.map(camera => (
                <option key={camera.id} value={camera.id}>
                  {camera.label || `Kamera ${camera.id}`}
                </option>
              ))}
            </select>
          </div>
        )}

        {cameraPermission === false && (
          <div className="text-center py-8">
            <Camera className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">
              Kein Zugriff auf die Kamera. Bitte erteilen Sie die Berechtigung in Ihren Browsereinstellungen oder verwenden Sie die Dateiauswahl.
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={requestCameraPermission}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                Kamerazugriff erlauben
              </button>
              
              <div className="relative">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                >
                  Bild auswählen
                </button>
              </div>
            </div>
          </div>
        )}

        {cameraPermission === null && (
          <div className="text-center py-8">
            <Loader className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Kamera wird initialisiert...</p>
          </div>
        )}

        {cameraPermission === true && (
          <>
            <p className="text-gray-600 mb-4">{description}</p>
            
            <div className="relative">
              <div 
                id={scannerContainerId} 
                className="w-full h-64 bg-gray-100 rounded-lg overflow-hidden"
              ></div>
              
              <div className="absolute inset-0 pointer-events-none border-2 border-blue-500 rounded-lg">
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-48 h-48 border-2 border-dashed border-blue-500 rounded-lg"></div>
              </div>
            </div>
            
            <div className="flex items-center justify-center gap-4 mt-4">
              <div className="flex items-center gap-1 text-gray-600">
                <QrCode className="w-5 h-5" />
                <span>QR-Codes</span>
              </div>
              <div className="flex items-center gap-1 text-gray-600">
                <Barcode className="w-5 h-5" />
                <span>Barcodes</span>
              </div>
            </div>
            
            <div className="mt-4">
              <p className="text-sm text-gray-500 mb-2">
                Falls die Kamera nicht funktioniert, können Sie auch ein Bild hochladen:
              </p>
              <div className="relative">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                >
                  Bild auswählen
                </button>
              </div>
            </div>
          </>
        )}

        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            Abbrechen
          </button>
        </div>
      </div>
    </div>
  );
};